import React, { lazy, Suspense, useEffect, useState, Fragment } from 'react'
import { withRouter } from 'react-router-dom'
import { Layout, Spin, Row, Button, Alert } from 'antd'
import { useDispatch } from 'react-redux'
import { appSelector } from '../helpers/appSelector'
import { AppDispatch } from '../helpers/appDispatch'
import {
  viewMerchantChannelPaymentsRequest,
  exportMerchantChannelPaymentsRequest,
  clearChannels
} from '../store/merchant-channels'
import { isEmpty } from '../helpers/isEmpty'
import { MerchantData } from '../interfaces'
import moment from 'moment'
import { useTranslation } from 'react-i18next'
import { getMerchantsRequest } from '../store/reports'

const Filters = lazy(() => import('../components/merchants-channels/Filters'))
const Cards = lazy(() => import('../components/merchants-channels/Cards'))
const Details = lazy(() => import('../components/merchants-channels/Details'))
const CurrencyFilter = lazy(
  () => import('../components/merchants-channels/CurrencyFilter')
)
const EmptyBox = lazy(() => import('../components/merchants-channels/EmptyBox'))

const { Content } = Layout

const MerchantsChannels = () => {
  const dispatch: AppDispatch = useDispatch()
  const { t } = useTranslation()
  const channelStore = appSelector((state) => state.channels)
  const reports = appSelector((state) => state.reports)
  const [exportType, setExportType] = useState('')
  const [fromDate, setFromDate] = useState('')
  const [toDate, setToDate] = useState('')
  const [merchant, setMerchant] = useState<MerchantData | null>(null)
  const [currency, setCurrency] = useState('USD')
  const [pageSize, setPageSize] = useState(10)
  const [skip, setSkip] = useState(0)
  const [isSearching, setIsSearching] = useState(false)
  const [currentPage, setCurrentPage] = useState(1)

  const {
    channels: channelReport,
    loading,
    isExporting,
    merchantChannelError,
    merchantChannelExportError
  } = channelStore
  const { merchants } = reports
  const channels = channelReport?.records || channelReport?.Records || []
  const channelSummary = channelReport?.summary || channelReport?.Summary
  const total =
    channelSummary?.recordsTotal ??
    channelSummary?.RecordsTotal ??
    channels.length

  useEffect(() => {
    const { merchants } = reports
    if (isEmpty(merchants)) {
      dispatch(getMerchantsRequest())
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const reloadChannels = () => {
    dispatch(
      viewMerchantChannelPaymentsRequest({
        currency,
        periodFrom: fromDate,
        periodTo: toDate,
        merchant: merchant?.name || null,
        exportType: '',
        pageSize,
        skip,
        fixedPeriod: 'overall',
        reportType: null,
        product: null,
        status: null,
        channel: null,
        searchValue: null
      })
    )
  }

  const onExportClick = (type: string) => {
    setExportType(type)
    dispatch(
      exportMerchantChannelPaymentsRequest({
        currency,
        periodFrom: fromDate,
        periodTo: toDate,
        merchant: merchant?.name || null,
        exportType: type,
        pageSize,
        skip,
        fixedPeriod: 'overall',
        reportType: null,
        product: null,
        status: null,
        channel: null,
        searchValue: null
      })
    )
  }

  const onLoadMore = (page: number, size: number) => {
    const newSkip = page - 1
    setPageSize(size)
    setSkip(newSkip)
    setCurrentPage(page)

    dispatch(
      viewMerchantChannelPaymentsRequest({
        currency,
        periodFrom: fromDate,
        periodTo: toDate,
        merchant: merchant?.name || null,
        exportType: '',
        pageSize: size,
        skip: newSkip,
        fixedPeriod: 'overall',
        reportType: null,
        product: null,
        status: null,
        channel: null,
        searchValue: null
      })
    )
  }

  const onReset = (form: any) => {
    form.resetFields()
    setFromDate('')
    setToDate('')
    setMerchant(null)
    setSkip(0)
    setCurrentPage(1)
    setIsSearching(false)
    setPageSize(10)

    dispatch(clearChannels())
  }

  const onSelectCurrency = (value: string) => {
    setCurrency(value)
    setSkip(0)
    setCurrentPage(1)

    if (isSearching) {
      dispatch(
        viewMerchantChannelPaymentsRequest({
          currency: value,
          periodFrom: fromDate,
          periodTo: toDate,
          merchant: merchant?.name || null,
          exportType: '',
          pageSize,
          skip: 0,
          fixedPeriod: 'overall',
          reportType: null,
          product: null,
          status: null,
          channel: null,
          searchValue: null
        })
      )
    }
  }

  const onSearch = (values: any) => {
    setIsSearching(true)
    setSkip(0)
    setCurrentPage(1)

    const { periodFrom, periodTo, merchant: merchantId } = values
    let m: MerchantData | undefined = undefined
    let pFrom: string = '',
      pTo: string = ''

    if (periodFrom !== undefined && periodTo !== undefined) {
      pFrom = moment(periodFrom).format('MM/DD/YYYY')
      pTo = moment(periodTo).format('MM/DD/YYYY')
      setFromDate(pFrom)
      setToDate(pTo)
    }

    if (merchantId !== undefined) {
      m = merchants.find((m) => m.merchantId === merchantId)
      setMerchant(m !== undefined ? m : null)
    } else {
      setMerchant(null)
    }

    const searchParams = {
      currency,
      periodFrom: pFrom,
      periodTo: pTo,
      merchant: m?.name || null,
      exportType: '',
      pageSize,
      skip: 0,
      fixedPeriod: 'overall',
      reportType: null,
      product: null,
      status: null,
      channel: null,
      searchValue: null
    }

    dispatch(viewMerchantChannelPaymentsRequest(searchParams))
  }

  return (
    <div className="padding-box">
      <Content className="site-layout-background site-box">
        <Suspense
          fallback={
            <Row className="suspense-container">
              <div style={{ marginTop: '200px' }}>
                <Spin />
              </div>
            </Row>
          }
        >
          <Fragment>
            <Filters
              onReset={onReset}
              onSearch={onSearch}
              merchants={merchants}
              translate={t}
            />
            {isSearching ? (
              <Fragment>
                {merchantChannelError && (
                  <Alert
                    message="Failed to fetch data"
                    description={
                      typeof merchantChannelError === 'string'
                        ? merchantChannelError
                        : merchantChannelError?.detail || 'An unknown error occurred'
                    }
                    type="error"
                    showIcon
                    style={{ marginTop: 10 }}
                  />
                )}
                {merchantChannelExportError && (
                  <Alert
                    message="Failed to export data"
                    description={
                      typeof merchantChannelExportError === 'string'
                        ? merchantChannelExportError
                        : merchantChannelExportError?.detail || 'An unknown error occurred'
                    }
                    type="error"
                    showIcon
                    style={{ marginTop: 10 }}
                  />
                )}
                <Cards
                  summary={channelSummary}
                  currency={currency}
                  loading={loading}
                  translate={t}
                />
                <div className="margin-top">
                  <Row style={{ position: 'relative' }}>
                    <h4 className="transaction-chart-text">
                      {t('general.Merchants Channels')}
                    </h4>
                    <div className="utility-buttons">
                      <>
                        <Button
                          type="primary"
                          className="export-buttons"
                          onClick={() => onExportClick('EXCEL')}
                          loading={isExporting && exportType === 'EXCEL'}
                          disabled={!channels.length}
                        >
                          {t('general.export-excel')}
                        </Button>
                      </>
                      <Button
                        type="primary"
                        className="export-buttons reload"
                        onClick={() => reloadChannels()}
                      >
                        {t('general.refresh')}
                      </Button>
                    </div>
                  </Row>
                  <CurrencyFilter
                    onSelectCurrency={onSelectCurrency}
                    translate={t}
                  />
                  <Details
                    channels={channels}
                    currency={currency}
                    loading={loading}
                    onLoadMore={onLoadMore}
                    total={total}
                    translate={t}
                    currentPage={currentPage}
                  />
                </div>
              </Fragment>
            ) : (
              <EmptyBox header={t('general.filterToGetData')} description="" />
            )}
          </Fragment>
        </Suspense>
      </Content>
    </div>
  )
}

export default withRouter(MerchantsChannels)
